import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import "./style.css";
import ContactSelectionModal from "./../ContactSelectionModal";

// Import custom components
import { ElementsChat, SurveyChat, StandardChat } from "./components";

// Import custom hooks
import { useTranscription, useChatHeight } from "./hooks";

/**
 * RelationShipChat - Main component for relationship chat interface
 * @param {Object} props - Component props
 */
function RelationShipChat({
  onEnterData,
  chatData,
  onOptionSelected,
  hideChatInput = false,
  isAiLoading,
  iselements,
  lastQuestID,
  restrictEnterManualData,
  isSurvey,
  isChatProgress,
  isChatLoading,
  hasSelectedDomain,
  loadLatestChatHistory,
  isPrivacyActivated,
  setIsPrivacyActivated,
  chatPlaceholder,
}) {
  // State for chat UI
  const [chatChange, setChatChange] = useState("");
  const [doEnterHandleSubmitAnswer, setDoEnterHandleSubmitAnswer] = useState(false);
  const [showRelationShipContacts, setShowRelationShipContacts] = useState(false);
  const [selectedRelationShipName, setSelectedRelationShipName] = useState(null);

  // Refs
  const inputContainerRef = useRef(null);
  const clearRef = useRef(null);
  const aiChatRef = useRef(null);
  const chatApiRef = useRef(null);

  // Custom hooks
  const { 
    transcript, 
    isRecording, 
    isConnecting, 
    clearTranscript, 
    updateTranscript, 
    toggleRecording,
    stopRecording 
  } = useTranscription();
  
  const { chatMaxHeight } = useChatHeight(inputContainerRef);

  // Event handlers

  const callChildFunction = () => {
    aiChatRef.current?.actionCallOnAnswerSubmit?.();
  };

  const handleNavigatetoElement = () => {
    window.location.href = "/elements";
  };

  const onCloseRelationshipAdvise = () => {
    setShowRelationShipContacts(false);
  };

  const onContactSelection = (contact) => {
    if (contact?.type === "addNewContact") {
      onEnterData({
        type: "initiateRelationship",
        subType: "addNewContact",
        text: "Add new relationShip",
      });
      // Clear relationId from URL when adding a new contact
      try {
        const url = new URL(window.location.href);
        url.pathname = "/chat";
        url.searchParams.delete("relationId");
        window.history.replaceState({}, "", url.toString());
      } catch (_) {}
    } else {
      setSelectedRelationShipName(contact);
      onEnterData({
        type: "initiateRelationship",
        text: "I need relationship advice for ",
        name: contact?.first_name + " " + contact?.last_name,
        profile_pic_url: contact?.profile_pic_url,
        contact_relational_data: contact?.related_data,
        relation_id: contact?._id,
        user1_id: contact?.user1_id,
        user2_id: contact?.user2_id,
      });
      // Ensure URL reflects the selected relationship for deep-linking and refresh
      try {
        const url = new URL(window.location.href);
        url.pathname = "/chat"; // normalize to chat route
        if (contact?._id) url.searchParams.set("relationId", contact._id);
        window.history.replaceState({}, "", url.toString());
      } catch (_) {}
    }
  };

  const afterOnPassData = () => {
    // Stop recording if it's active
    if (isRecording) {
      stopRecording();
    }
  };

  const changeData = (data) => {
    setChatChange(data);
    updateTranscript(data);
  };

  const clearManualData = () => {
    clearRef?.current?.clearInput();
    setChatChange("");
  };

  const onEnterDataPicker = (e) => {
    if (doEnterHandleSubmitAnswer) {
      callChildFunction();
      clearTranscript();
      setDoEnterHandleSubmitAnswer(false);
    } else {
      onEnterData(e);
      clearManualData();
      clearTranscript();
    }
  };

  const handleEnterWithSubmit = () => {
    setDoEnterHandleSubmitAnswer(true);
  };

  const onPrivacyClick = () => {
    setIsPrivacyActivated((prev) => !prev);
  };

  const startRecording = () => {
    toggleRecording(isChatLoading);
  };

  // Render the appropriate chat component based on mode
  if (iselements) {
    // Use StandardChat layout/UI for Elements mode, keeping intro/options behavior via chatData
    return (
      <StandardChat
        chatData={chatData}
        onOptionSelected={onOptionSelected}
        isAiLoading={isAiLoading}
        transcript={transcript}
        chatChange={chatChange}
        clearManualData={clearManualData}
        lastQuestID={lastQuestID}
        handleEnterWithSubmit={handleEnterWithSubmit}
        onEnterDataPicker={onEnterDataPicker}
        afterOnPassData={afterOnPassData}
        changeData={changeData}
        restrictEnterManualData={restrictEnterManualData}
        isChatProgress={isChatProgress}
        isChatLoading={isChatLoading}
        hideChatInput={hideChatInput}
        loadLatestChatHistory={loadLatestChatHistory}
        chatMaxHeight={chatMaxHeight}
        isRecording={isRecording}
        isConnecting={isConnecting}
        startRecording={startRecording}
        isPrivacyActivated={isPrivacyActivated}
        onPrivacyClick={onPrivacyClick}
        handleNavigatetoElement={handleNavigatetoElement}
        setShowRelationShipContacts={setShowRelationShipContacts}
        clearRef={clearRef}
        chatPlaceholder={chatPlaceholder}
        aiChatRef={aiChatRef}
        inputContainerRef={inputContainerRef}
        chatApiRef={chatApiRef}
      />
    );
  }

  if (isSurvey) {
    return (
      <SurveyChat
        chatData={chatData}
        onOptionSelected={onOptionSelected}
        isAiLoading={isAiLoading}
        transcript={transcript}
        chatChange={chatChange}
        clearManualData={clearManualData}
        lastQuestID={lastQuestID}
        handleEnterWithSubmit={handleEnterWithSubmit}
        onEnterDataPicker={onEnterDataPicker}
        afterOnPassData={afterOnPassData}
        changeData={changeData}
        restrictEnterManualData={restrictEnterManualData}
        isChatProgress={isChatProgress}
        isChatLoading={isChatLoading}
        hideChatInput={hideChatInput}
        loadLatestChatHistory={loadLatestChatHistory}
        chatMaxHeight={chatMaxHeight}
        clearRef={clearRef}
        aiChatRef={aiChatRef}
        inputContainerRef={inputContainerRef}
      />
    );
  }

  return (
    <>
      <StandardChat
        chatData={chatData}
        onOptionSelected={onOptionSelected}
        isAiLoading={isAiLoading}
        transcript={transcript}
        chatChange={chatChange}
        clearManualData={clearManualData}
        lastQuestID={lastQuestID}
        handleEnterWithSubmit={handleEnterWithSubmit}
        onEnterDataPicker={onEnterDataPicker}
        afterOnPassData={afterOnPassData}
        changeData={changeData}
        restrictEnterManualData={restrictEnterManualData}
        isChatProgress={isChatProgress}
        isChatLoading={isChatLoading}
        hideChatInput={hideChatInput}
        loadLatestChatHistory={loadLatestChatHistory}
        chatMaxHeight={chatMaxHeight}
        isRecording={isRecording}
        isConnecting={isConnecting}
        startRecording={startRecording}
        isPrivacyActivated={isPrivacyActivated}
        onPrivacyClick={onPrivacyClick}
        handleNavigatetoElement={handleNavigatetoElement}
        setShowRelationShipContacts={setShowRelationShipContacts}
        clearRef={clearRef}
        chatPlaceholder={chatPlaceholder}
        aiChatRef={aiChatRef}
        inputContainerRef={inputContainerRef}
        chatApiRef={chatApiRef}
      />
      <ContactSelectionModal
        isOpen={showRelationShipContacts}
        onClose={onCloseRelationshipAdvise}
        onContactSelection={onContactSelection}
      />
    </>
  );
}

RelationShipChat.propTypes = {
  chatPlaceholder: PropTypes.string,
  onEnterData: PropTypes.func.isRequired,
  chatData: PropTypes.array.isRequired,
  onOptionSelected: PropTypes.func.isRequired,
  hideChatInput: PropTypes.bool,
  isAiLoading: PropTypes.bool,
  iselements: PropTypes.bool,
  lastQuestID: PropTypes.string,
  restrictEnterManualData: PropTypes.bool,
  isSurvey: PropTypes.bool,
  isChatProgress: PropTypes.bool,
  isChatLoading: PropTypes.bool,
  hasSelectedDomain: PropTypes.bool,
  loadLatestChatHistory: PropTypes.func,
  isPrivacyActivated: PropTypes.bool,
  setIsPrivacyActivated: PropTypes.func
};

export default RelationShipChat;
